<template>
  <GuestAddPopup :school="propsSchools" />

  <div class="section-block">
    <div class="tabs-search-bar">
      <div class="tabs-scroll-wrap">
        <v-tabs v-model="tab" align-tabs="left" color="primary" style="min-width: 320px">
          <v-tab @click="goall()" :value="1" class="tabstyle">전체</v-tab>
          <v-tab @click="onClickFree" :value="2" class="tabstyle">무료 체험</v-tab>
          <v-tab @click="onClickTeacher" :value="3" class="tabstyle">교사 연수</v-tab>
          <v-tab :value="10" class="tabstyle">검색 결과</v-tab>
        </v-tabs>
      </div>

      <div class="search-main">
        <div class="count-search">
          <b>{{ pad(tableItems.length) }}</b>건
        </div>
        <div class="search-field">
          <div class="search-label">게스트 명 / 코드</div>
          <div style="width: 220px">
            <v-text-field
              v-model="searchtext"
              append-inner-icon="mdi-magnify"
              density="compact"
              variant="outlined"
              color="primary"
              placeholder="학교명 또는 코드 입력"
              spellcheck="false"
              hide-details
              clearable
            />
          </div>
        </div>
      </div>
    </div>

    <!-- 정렬 / 필터 -->
    <div class="filter-bar">
      <div class="filter-bar__group">
        <span class="filter-bar__label">정렬</span>
        <v-select
          v-model="sortBy"
          :items="sortOptions"
          item-title="label"
          item-value="value"
          density="compact"
          variant="outlined"
          hide-details
          style="min-width: 160px"
        />
      </div>
      <v-checkbox
        v-model="hideExpired"
        label="만료 학교 숨기기"
        density="compact"
        color="primary"
        hide-details
      />
    </div>
  </div>

  <!-- 데스크탑/태블릿 ≥640px: 테이블 -->
  <DataTable
    v-if="!isMobile"
    :headers="tableHeaders"
    :items="tableItems"
    :items-per-page="15"
    row-key="schoolCode"
    density="comfortable"
  >
    <template #toolbar>
      <span class="table-count"><b>{{ pad(tableItems.length) }}</b>건</span>
      <div class="toolbar-spacer" />
      <v-btn
        @click="onClickAdd"
        variant="flat"
        color="primary"
        size="small"
      >
        GUEST 등록
      </v-btn>
    </template>

    <template #item.schoolName="{ item }">
      <router-link
        :to="`/guest/guest-manage/${item.schoolCode}`"
        @click="onClickLink(item.schoolCode)"
        class="cell-link"
      >
        {{ item.schoolName }}
      </router-link>
    </template>

    <template #item.schoolCode="{ item }">
      <span class="mono-code">{{ item.schoolCode }}</span>
    </template>

    <template #item.startDate="{ item }">
      {{ formatDate(item.startDate) }}
    </template>

    <template #item.endDate="{ item }">
      <span :class="{ 'cell-expired': checkDate(item.endDate) }">
        {{ formatDate(item.endDate) }}
        <span v-if="checkDate(item.endDate)" class="expired-flag">(만료)</span>
      </span>
    </template>

    <template #item.memo="{ item }">
      <span class="cell-memo">{{ item.memo }}</span>
    </template>

    <template #empty>
      <EmptyState
        title="등록된 게스트 학교/단체가 없습니다"
        description="우측 상단의 'GUEST 등록' 버튼으로 추가하세요."
        icon="mdi-school-outline"
      />
    </template>
  </DataTable>

  <!-- 모바일 ≤640px: 카드 리스트 -->
  <div v-else class="mobile-list">
    <div class="mobile-list__toolbar">
      <span class="table-count"><b>{{ pad(tableItems.length) }}</b>건</span>
      <v-btn
        @click="onClickAdd"
        variant="flat"
        color="primary"
        size="small"
      >
        GUEST 등록
      </v-btn>
    </div>

    <div v-if="tableItems.length === 0" class="mobile-empty">
      <EmptyState
        title="등록된 게스트 학교/단체가 없습니다"
        description="상단의 'GUEST 등록' 버튼으로 추가하세요."
        icon="mdi-school-outline"
      />
    </div>

    <router-link
      v-for="item in pagedItems"
      :key="item.schoolCode"
      :to="`/guest/guest-manage/${item.schoolCode}`"
      @click="onClickLink(item.schoolCode)"
      class="guest-card"
    >
      <header class="guest-card__head">
        <div class="guest-card__avatar" aria-hidden="true">
          <v-icon icon="mdi-school" size="20" />
        </div>
        <div class="guest-card__heading">
          <h3 class="guest-card__name">{{ item.schoolName }}</h3>
          <p class="guest-card__meta">
            <span class="mono">{{ item.schoolCode }}</span>
            <span class="guest-card__dot">·</span>
            <span>No. {{ item.no }}</span>
          </p>
        </div>
        <span
          class="guest-card__type"
          :class="item.type === '교사 연수' ? 'guest-card__type--training' : 'guest-card__type--free'"
        >
          {{ item.type || '무료 체험' }}
        </span>
      </header>

      <div class="guest-card__dates">
        <div class="guest-card__date-item">
          <v-icon icon="mdi-calendar-start" size="14" class="guest-card__date-icon" />
          <span class="guest-card__date-label">시작</span>
          <span class="tabular-nums mono">{{ formatDate(item.startDate) }}</span>
        </div>
        <div class="guest-card__date-divider" />
        <div class="guest-card__date-item">
          <v-icon icon="mdi-calendar-end" size="14" class="guest-card__date-icon" />
          <span class="guest-card__date-label">만료</span>
          <span
            class="tabular-nums mono"
            :class="{ 'cell-expired': checkDate(item.endDate) }"
          >
            {{ formatDate(item.endDate) }}
          </span>
          <span v-if="checkDate(item.endDate)" class="guest-card__expired-flag">만료됨</span>
        </div>
      </div>

      <div class="guest-card__chips">
        <div class="guest-card__chip">
          <v-icon icon="mdi-key-outline" size="14" />
          초기비번 <span class="mono">{{ item.settingPassword || '-' }}</span>
        </div>
        <div v-if="item.memo" class="guest-card__chip guest-card__chip--memo">
          <v-icon icon="mdi-note-text-outline" size="14" />
          <span class="guest-card__memo">{{ item.memo }}</span>
        </div>
      </div>
    </router-link>

    <div v-if="totalPages > 1" class="mobile-pagination">
      <v-pagination
        v-model="mobilePage"
        :length="totalPages"
        :total-visible="5"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onBeforeMount, onMounted, onBeforeUnmount, watch, computed } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";

import dayjs from "dayjs";
import GuestAddPopup from "../../popup/GuestAddPopupComponent.vue";
import DataTable from "@/components/ui/DataTable.vue";
import EmptyState from "@/components/ui/EmptyState.vue";

let tab = ref(null);

const schools = ref([]);
const searchResult = ref([]);
const filterSearch = ref([]);
const propsSchools = ref([]);
const searchData = ref({});
const searchList = ref([]);
const searchtext = ref("");
const selectedSchoolCode = ref("");

const store = useStore();
// eslint-disable-next-line no-unused-vars
const router = useRouter();

const tableHeaders = [
  { title: "No", key: "no", sortable: false, align: "center", width: 60 },
  { title: "게스트 명", key: "schoolName", sortable: true, align: "center" },
  { title: "코드", key: "schoolCode", sortable: true, align: "center" },
  { title: "구분", key: "type", sortable: true, align: "center" },
  { title: "계약시작일", key: "startDate", sortable: true, align: "center" },
  { title: "계약만료일", key: "endDate", sortable: true, align: "center" },
  { title: "초기비번", key: "settingPassword", sortable: false, align: "center" },
  { title: "메모", key: "memo", sortable: false, align: "left" },
];

const tableItems = ref([]);

const windowWidth = ref(typeof window !== "undefined" ? window.innerWidth : 1024);
const isMobile = computed(() => windowWidth.value <= 640);
const onResize = () => { windowWidth.value = window.innerWidth; };

const mobilePage = ref(1);
const mobilePageSize = 10;
const totalPages = computed(() => Math.max(1, Math.ceil(tableItems.value.length / mobilePageSize)));
const pagedItems = computed(() => {
  const start = (mobilePage.value - 1) * mobilePageSize;
  return tableItems.value.slice(start, start + mobilePageSize);
});

// 날짜 유틸 (sortAndFilter가 사용하므로 위에 선언)
const formatDate = (d) => (d ? dayjs(d).format("YYYY/MM/DD") : "-");
const checkDate = (date) => {
  if (!date) return false;
  const today = dayjs().format("YYYY.MM.DD");
  const getDate = dayjs(date).format("YYYY.MM.DD");
  return dayjs(getDate).isBefore(dayjs(today));
};
const pad = (num) => (num < 10 ? num.toString().padStart(2, "0") : num);

// 정렬 / 필터
const sortBy = ref("recent");
const hideExpired = ref(false);
const sortOptions = [
  { label: "최신 등록순", value: "recent" },
  { label: "만료 임박순", value: "expiringSoon" },
  { label: "가나다순", value: "alphabetical" },
];

// 학교 생성 시각 추출. createdAt → _id timestamp 순으로 fallback.
const createdAtMillis = (school) => {
  if (school?.createdAt) {
    const t = dayjs(school.createdAt).valueOf();
    if (!Number.isNaN(t)) return t;
  }
  const id = school?._id;
  if (typeof id === "string" && id.length >= 8) {
    const secs = parseInt(id.substring(0, 8), 16);
    if (Number.isFinite(secs)) return secs * 1000;
  }
  return 0;
};

const sortAndFilter = (rows) => {
  if (!Array.isArray(rows)) return [];
  let list = [...rows];

  if (hideExpired.value) {
    list = list.filter((s) => !checkDate(s.endDate));
  }

  if (sortBy.value === "alphabetical") {
    list.sort((a, b) => (a.schoolName || "").localeCompare(b.schoolName || "", "ko"));
  } else if (sortBy.value === "expiringSoon") {
    list.sort((a, b) => {
      const aExpired = checkDate(a.endDate);
      const bExpired = checkDate(b.endDate);
      if (aExpired !== bExpired) return aExpired ? 1 : -1;
      const aTime = a.endDate ? dayjs(a.endDate).valueOf() : Infinity;
      const bTime = b.endDate ? dayjs(b.endDate).valueOf() : Infinity;
      return aTime - bTime;
    });
  } else {
    list.sort((a, b) => createdAtMillis(b) - createdAtMillis(a));
  }
  return list;
};

watch([filterSearch, sortBy, hideExpired], ([val]) => {
  const sorted = sortAndFilter(val);
  tableItems.value = sorted.map((s, idx) => ({ ...s, no: idx + 1 }));
  mobilePage.value = 1;
}, { immediate: true });

onMounted(() => {
  window.addEventListener("resize", onResize);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", onResize);
});

watch(tab, () => {
  // 탭 변경 시 페이지 초기화 효과 (DataTable 내부에서 처리)
});

const onClickAdd = () => {
  store.commit("SET_GUSET_ADD_POPUP_VIEW", true);
};

const onClickLink = (code) => {
  sessionStorage.setItem("schoolCode", JSON.stringify(code));
};

onBeforeMount(async () => {
  const res = await store.dispatch("GET_SCHOOL_GUEST");
  if (res.result === true) {
    schools.value = res.data;
    searchResult.value = res.data;
    filterSearch.value = searchResult.value;
  }
  propsSchools.value = Object.values(schools.value).map((obj) =>
    obj.schoolName.replace("초등학교", "")
  );

  const { data } = await store.dispatch("GET_SEARCH_GUEST");
  searchData.value = data;
  searchList.value = Object.getOwnPropertyNames(data);
});

// substring 매칭 — 학교명 또는 코드에 검색어가 포함된 학교 필터링
const runSearch = () => {
  const raw = (searchtext.value || "").trim();
  if (raw === "") {
    // 빈 입력 → 전체로 복귀
    searchResult.value = schools.value;
    filterSearch.value = searchResult.value;
    tab.value = 1;
    return;
  }
  const needle = raw.toLowerCase();
  const result = (schools.value || []).filter((item) => {
    const name = (item.schoolName || "").toLowerCase();
    const code = (item.schoolCode || "").toLowerCase();
    return name.includes(needle) || code.includes(needle);
  });
  searchResult.value = result;
  filterSearch.value = result;
  tab.value = 10;
};

// 입력 시 디바운스 200ms 후 즉시 검색
let searchTimer = null;
watch(searchtext, () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(runSearch, 200);
});

// Enter / 외부 호출용 (호환 유지)
const search = runSearch;

const goall = () => {
  searchResult.value = schools.value;
  filterSearch.value = searchResult.value;
  tab.value = 1;
};

const onClickFree = () => {
  const data = searchResult.value.filter((v) => v.type !== "교사 연수");
  filterSearch.value = data;
};

const onClickTeacher = () => {
  const data = searchResult.value.filter((v) => v.type === "교사 연수");
  filterSearch.value = data;
};
</script>

<style scoped>
.section-block {
  margin-bottom: var(--space-4);
}

.tabs-search-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--space-3);
}

.filter-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-4);
  margin-top: var(--space-3);
  padding-top: var(--space-3);
  border-top: 1px solid var(--border-default);
}

.filter-bar__group {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.filter-bar__label {
  font-size: var(--font-small-size);
  color: var(--text-tertiary);
  font-weight: 500;
}

.tabs-scroll-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.tabstyle {
  font-size: var(--font-small-size);
  font-weight: 600;
  color: var(--text-tertiary);
}

.search-main {
  display: flex;
  align-items: flex-end;
  gap: var(--space-3);
}

.count-search {
  font-size: var(--font-small-size);
  color: var(--text-secondary);
  margin-bottom: var(--space-2);
}

.search-label {
  font-size: var(--font-caption-size);
  color: var(--text-tertiary);
  margin-bottom: var(--space-1);
}

.table-count {
  font-size: var(--font-body-size);
  color: var(--text-secondary);
}
.toolbar-spacer {
  flex: 1;
}

.cell-link {
  color: var(--color-primary-600);
  text-decoration: none;
  font-weight: 500;
}
.cell-link:hover {
  text-decoration: underline;
}

.mono-code {
  font-family: var(--font-family-mono);
  font-size: var(--font-mono-size);
  color: var(--text-primary);
}

.cell-expired {
  color: var(--color-error-text);
  font-weight: 600;
}
.expired-flag {
  margin-left: var(--space-1);
  font-size: var(--font-caption-size);
}

.cell-memo {
  display: inline-block;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-secondary);
  font-size: var(--font-small-size);
}

/* ─── 모바일 카드 리스트 ─── */
.mobile-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.mobile-list__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
}

.mobile-empty {
  padding: var(--space-6) 0;
}

.guest-card {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
  background: var(--bg-surface);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  text-decoration: none;
  color: inherit;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
}

.guest-card:hover {
  box-shadow: var(--shadow-md);
  border-color: var(--color-primary-300);
}

.guest-card__head {
  display: grid;
  grid-template-columns: 40px 1fr auto;
  gap: var(--space-3);
  align-items: center;
}

.guest-card__avatar {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-primary-50);
  color: var(--color-primary-600);
  border-radius: var(--radius-md);
  flex-shrink: 0;
}

.guest-card__heading {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.guest-card__name {
  margin: 0;
  font-size: var(--font-body-strong-size);
  font-weight: 700;
  color: var(--text-primary);
  word-break: keep-all;
  line-height: 1.3;
}

.guest-card__meta {
  margin: 0;
  display: flex;
  align-items: center;
  gap: var(--space-1);
  font-size: var(--font-caption-size);
  color: var(--text-tertiary);
  flex-wrap: wrap;
}

.guest-card__dot {
  color: var(--border-strong);
}

.mono {
  font-family: var(--font-family-mono);
  font-size: var(--font-mono-size);
}

/* 타입 칩 */
.guest-card__type {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 var(--space-2);
  border-radius: var(--radius-pill);
  font-size: var(--font-caption-size);
  font-weight: 600;
  white-space: nowrap;
  border: 1px solid transparent;
}

.guest-card__type--free {
  background: var(--color-info-bg);
  color: var(--color-info-text);
  border-color: var(--color-info-border);
}

.guest-card__type--training {
  background: var(--color-warning-bg);
  color: var(--color-warning-text);
  border-color: var(--color-warning-border);
}

/* 날짜 영역 */
.guest-card__dates {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3);
  background: var(--bg-canvas);
  border-radius: var(--radius-md);
  font-size: var(--font-caption-size);
  flex-wrap: wrap;
}

.guest-card__date-item {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1 1 0;
  min-width: 0;
}

.guest-card__date-icon {
  color: var(--text-tertiary);
  flex-shrink: 0;
}

.guest-card__date-label {
  color: var(--text-tertiary);
  font-weight: 500;
  flex-shrink: 0;
}

.guest-card__date-divider {
  width: 12px;
  height: 1px;
  background: var(--border-strong);
  flex-shrink: 0;
}

.guest-card__expired-flag {
  font-size: var(--font-caption-size);
  color: var(--color-error-text);
  font-weight: 600;
  margin-left: 4px;
}

/* 칩 */
.guest-card__chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.guest-card__chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 26px;
  padding: 0 var(--space-2);
  background: var(--bg-subtle);
  color: var(--text-secondary);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-pill);
  font-size: var(--font-caption-size);
  font-weight: 500;
}

.guest-card__chip--memo {
  max-width: 100%;
}

.guest-card__memo {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
}

.mobile-pagination {
  display: flex;
  justify-content: center;
  margin-top: var(--space-3);
}
</style>
